import {
  Button,
  cn,
  HorizontalMenu,
  Icons,
  Skeleton,
} from "@win-reality/react-ui";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { useField } from "@/hooks/useComplexes";
import { ROUTES } from "@/lib/routes";

import { FieldConfiguration } from "./FieldConfiguration/FieldConfiguration";
import { FieldSchedule } from "./FieldSchedule/FieldSchedule";

const TABS = [
  { label: "Schedule", key: "schedule" },
  { label: "Configuration", key: "configuration" },
] as const;

function SingleField() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: "schedule" | "configuration" =
    searchParams.get("tab") === "configuration" ? "configuration" : "schedule";
  const setTab = (next: "schedule" | "configuration") => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "schedule") {
          params.delete("tab");
        } else {
          params.set("tab", next);
        }
        return params;
      },
      { replace: true },
    );
  };
  const location = useLocation();
  const navigate = useNavigate();

  const { complexName, fieldId } = useParams<{
    complexName: string;
    fieldId: string;
  }>();

  if (!complexName || !fieldId) {
    throw new Error("Missing complexName or fieldId in URL parameters");
  }

  const fromManageSystems =
    (location.state as { fromManageSystems?: boolean })?.fromManageSystems ??
    false;

  const handleBackClick = () => {
    if (fromManageSystems) {
      navigate(ROUTES.MANAGE_SMARTPARKS_ADMIN);
    } else {
      navigate(
        ROUTES.MANAGE_COMPLEX.replace(":complexName", complexName || ""),
      );
    }
  };

  const {
    field,
    unifiedConfig,
    strikeZoneConfig,
    isLoadingUnifiedConfig,
    isLoadingStrikeZoneConfig,
    isLoadingField,
  } = useField(fieldId ?? "");

  return (
    <div
      className={cn(
        "min-h-full",
        tab === "schedule" ? "bg-gray-100" : "bg-white",
      )}
    >
      <div className="sticky top-0 z-20 bg-white">
        <div className="pt-4 pb-4 max-w-130 mx-auto px-5 w-full grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div className="flex justify-start">
            <Button
              variant="secondary-flat-dark"
              size="icon-mini"
              onClick={handleBackClick}
            >
              <Icons.ArrowLeftMini />
            </Button>
          </div>

          <div className="text-center text-base">
            {field?.name ? (
              field.name
            ) : (
              <Skeleton className="rounded h-6 w-80 m-auto" />
            )}
          </div>

          <div className="flex justify-end"></div>
        </div>
      </div>

      <div className="sticky top-18 z-10 bg-white flex flex-col justify-center min-w-0">
        <HorizontalMenu
          size="md"
          wrapperClassName="w-full [&>*]:mx-auto"
          items={TABS.map((t) => ({
            label: t.label,
            active: tab === t.key,
            onClick: () => setTab(t.key),
          }))}
        />

        <div className="absolute top-6.5 left-0 h-2 w-full bg-linear-to-b from-black/4 to-transparent"></div>
      </div>

      {tab === "schedule" && (
        <FieldSchedule
          complexName={complexName}
          machineField={field}
          isLoading={isLoadingUnifiedConfig || isLoadingField}
        />
      )}

      {tab === "configuration" && (
        <FieldConfiguration
          field={field}
          unifiedConfig={unifiedConfig}
          strikeZoneConfig={strikeZoneConfig}
          isLoadingUnifiedConfig={isLoadingUnifiedConfig}
          isLoadingStrikeZoneConfig={isLoadingStrikeZoneConfig}
        />
      )}
    </div>
  );
}

export { SingleField };
